import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiPlus, FiEye, FiCalendar, FiStar, FiTrendingUp, FiEdit, FiTrash2 } = FiIcons;

const Services = () => {
  const [services] = useState([
    {
      id: 1,
      title: 'Home Cleaning',
      price: '$120',
      category: 'Cleaning',
      image: 'https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=400&h=300&fit=crop',
      views: 342,
      bookings: 28,
      rating: 4.9,
      conversionRate: 8.2
    },
    {
      id: 2,
      title: 'Garden Maintenance',
      price: '$85',
      category: 'Gardening',
      image: 'https://images.unsplash.com/photo-1416879595882-3373a0480b5b?w=400&h=300&fit=crop',
      views: 198,
      bookings: 15,
      rating: 4.7,
      conversionRate: 7.6
    },
    {
      id: 3,
      title: 'Pet Sitting',
      price: '$60',
      category: 'Pet Care',
      image: 'https://images.unsplash.com/photo-1601758228041-f3b2795255f1?w=400&h=300&fit=crop',
      views: 156,
      bookings: 12,
      rating: 5.0,
      conversionRate: 7.7
    },
    {
      id: 4,
      title: 'Math Tutoring',
      price: '$45',
      category: 'Education',
      image: 'https://images.unsplash.com/photo-1509062522246-3755977927d7?w=400&h=300&fit=crop',
      views: 89,
      bookings: 8,
      rating: 4.8,
      conversionRate: 9.0
    }
  ]);

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <motion.div 
        className="flex items-center justify-between"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div>
          <h1 className="text-3xl font-bold text-slate-800">Services</h1>
          <p className="text-slate-600 mt-1">Track and edit your service offerings</p>
        </div>
        
        <motion.button
          className="flex items-center gap-2 bg-trust-500 text-white px-6 py-3 rounded-lg hover:bg-trust-600 transition-colors"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <SafeIcon icon={FiPlus} />
          Add New Service
        </motion.button>
      </motion.div>

      {/* Services Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {services.map((service, index) => (
          <motion.div
            key={service.id}
            className="bg-white rounded-xl overflow-hidden border border-slate-200 hover:shadow-lg transition-shadow"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
          >
            {/* Service Image */}
            <div className="relative h-48 overflow-hidden">
              <img 
                src={service.image} 
                alt={service.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute top-3 left-3">
                <span className="bg-white/90 text-slate-700 px-2 py-1 rounded-full text-xs font-medium">
                  {service.category}
                </span>
              </div>
              <div className="absolute top-3 right-3">
                <span className="bg-trust-500 text-white px-2 py-1 rounded-full text-sm font-bold">
                  {service.price}
                </span>
              </div>
            </div>

            {/* Service Details */}
            <div className="p-6">
              <h3 className="text-xl font-semibold text-slate-800 mb-3">{service.title}</h3>
              
              {/* Stats Grid */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="flex items-center gap-2">
                  <SafeIcon icon={FiEye} className="text-slate-500" />
                  <div>
                    <div className="text-sm font-medium text-slate-800">{service.views}</div>
                    <div className="text-xs text-slate-500">Views</div>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <SafeIcon icon={FiCalendar} className="text-slate-500" />
                  <div>
                    <div className="text-sm font-medium text-slate-800">{service.bookings}</div>
                    <div className="text-xs text-slate-500">Bookings</div>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <SafeIcon icon={FiStar} className="text-yellow-500" />
                  <div>
                    <div className="text-sm font-medium text-slate-800">{service.rating}</div>
                    <div className="text-xs text-slate-500">Rating</div>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <SafeIcon icon={FiTrendingUp} className="text-green-500" />
                  <div>
                    <div className="text-sm font-medium text-slate-800">{service.conversionRate}%</div>
                    <div className="text-xs text-slate-500">Conversion</div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2">
                <motion.button
                  className="flex-1 flex items-center justify-center gap-2 bg-trust-500 text-white py-2 px-4 rounded-lg hover:bg-trust-600 transition-colors"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <SafeIcon icon={FiEdit} className="text-sm" />
                  Edit
                </motion.button>
                <motion.button
                  className="p-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <SafeIcon icon={FiTrash2} className="text-sm" />
                </motion.button>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Add Service Card */}
      <motion.div
        className="bg-slate-50 border-2 border-dashed border-slate-300 rounded-xl p-8 text-center hover:border-trust-400 hover:bg-trust-50 transition-all cursor-pointer"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.5 }}
        whileHover={{ scale: 1.02 }}
      >
        <SafeIcon icon={FiPlus} className="text-4xl text-slate-400 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-slate-600 mb-2">Add New Service</h3>
        <p className="text-slate-500">Create a new service offering to expand your business</p>
      </motion.div>
    </div>
  );
};

export default Services;